"use client";

import * as React from "react";
import Link from "next/link";
import { ArrowRight, Check, X } from "lucide-react";
import { Badge } from "@modernatc/ui/badge";
import { Button } from "@modernatc/ui/button";
import { Callsign } from "@modernatc/ui/callsign";
import { cn } from "@modernatc/ui/cn";

interface DemoCard {
  citation: string;
  kind: string;
  stem: string;
  choices: Array<{ id: string; text: string; correct: boolean }>;
  explanation: string;
}

/**
 * Top-of-funnel "try a card" interactive demo. Three real .65-style
 * questions rotate; the user can pick an answer, see the correct
 * choice + explanation, and step through. No signup, no DB writes,
 * no FSRS state — pure client-side. The point is to give visitors a
 * tangible feel for the product before they're asked to pay
 * anything.
 *
 * Cards are intentionally short and high-signal. Don't make this
 * harder than the actual product — visitors should feel "oh, I get
 * how this works."
 */

const CARDS: DemoCard[] = [
  {
    citation: "JO 7110.65BB · 3-9-1",
    kind: "phraseology",
    stem: "An aircraft is on a 5-mile final to runway 09. You're working tower and want to clear them to land. Pick the correct phraseology.",
    choices: [
      {
        id: "a",
        text: '"Cleared to land runway 09."',
        correct: false,
      },
      {
        id: "b",
        text: '"Cleared for landing 09."',
        correct: false,
      },
      {
        id: "c",
        text: '"Runway 09, cleared to land."',
        correct: true,
      },
      {
        id: "d",
        text: '"Land runway 09."',
        correct: false,
      },
    ],
    explanation:
      "The .65 standard structure is runway identifier first, then the clearance: “Runway 09, cleared to land.” Other forms appear in real-world traffic but the test-correct phraseology is the one that matches the .65 example exactly.",
  },
  {
    citation: "Pilot/Controller Glossary",
    kind: "definition",
    stem: 'What does "say again" mean when used by ATC?',
    choices: [
      {
        id: "a",
        text: "Repeat your last transmission.",
        correct: true,
      },
      {
        id: "b",
        text: "Confirm the previous instruction.",
        correct: false,
      },
      {
        id: "c",
        text: "Read back the last clearance.",
        correct: false,
      },
      {
        id: "d",
        text: "Stand by for repeat instructions.",
        correct: false,
      },
    ],
    explanation:
      "Per the Pilot/Controller Glossary: “SAY AGAIN — used to request a repeat of the last transmission.” Often confused with “confirm” (verify) or “say again all after” (partial repeat).",
  },
  {
    citation: "JO 7110.65BB · 5-5-4",
    kind: "separation",
    stem: "Two IFR aircraft at the same altitude, on opposite-direction tracks, both Category III heavy. Minimum lateral separation under radar?",
    choices: [
      { id: "a", text: "3 NM", correct: false },
      { id: "b", text: "5 NM", correct: true },
      { id: "c", text: "10 NM", correct: false },
      { id: "d", text: "Wake-turbulence minima only", correct: false },
    ],
    explanation:
      "5 NM is the standard radar separation between IFR aircraft above FL180 (and at lower altitudes outside terminal areas). Wake-turbulence minima apply additionally when same-direction same-route, but the floor for opposite-direction is the standard 5 NM lateral.",
  },
];

type Phase = "answering" | "revealed";

interface TryACardProps {
  /** URL the "Sign up" CTA should link to (signup on the app subdomain). */
  signupHref: string;
}

export function TryACard({ signupHref }: TryACardProps) {
  const [index, setIndex] = React.useState(0);
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [phase, setPhase] = React.useState<Phase>("answering");

  const card = CARDS[index]!;
  const correctChoice = card.choices.find((c) => c.correct)!;
  const isLast = index === CARDS.length - 1;

  function pick(id: string) {
    if (phase === "revealed") return;
    setSelectedId(id);
    setPhase("revealed");
  }

  function next() {
    if (isLast) return;
    setIndex((i) => Math.min(CARDS.length - 1, i + 1));
    setSelectedId(null);
    setPhase("answering");
  }

  function restart() {
    setIndex(0);
    setSelectedId(null);
    setPhase("answering");
  }

  const wasCorrect = phase === "revealed" && selectedId === correctChoice.id;

  return (
    <section
      id="try-it"
      aria-label="Try a sample question"
      className="border-b border-[var(--color-panel-edge)] py-20"
    >
      <div className="mx-auto max-w-3xl px-4 sm:px-6">
        <Callsign className="text-xs text-[var(--color-amber)]">
          § 02 · Try one
        </Callsign>
        <h2 className="mt-4 text-balance text-3xl font-semibold tracking-tight text-[var(--color-ink)] sm:text-4xl">
          Three real cards. No signup.
        </h2>
        <p className="mt-3 max-w-xl text-pretty text-[var(--color-ink-dim)]">
          A tiny taste of what your daily queue feels like.
          {" "}
          {index + 1} of {CARDS.length}.
        </p>

        <article className="panel-grain mt-10 rounded-[var(--radius-md)] border border-[var(--color-panel-edge)] bg-[var(--color-panel)] p-7 shadow-[var(--shadow-panel)]">
          <div className="flex items-center justify-between gap-3">
            <Callsign className="text-[11px] text-[var(--color-amber-bright)]">
              {card.citation}
            </Callsign>
            <Badge variant="muted">{card.kind}</Badge>
          </div>

          <h3 className="mt-5 text-balance text-lg font-medium leading-relaxed text-[var(--color-ink)] sm:text-xl">
            {card.stem}
          </h3>

          <ol className="mt-6 flex flex-col gap-2">
            {card.choices.map((choice, i) => {
              const isSelected = selectedId === choice.id;
              const showCorrect = phase === "revealed" && choice.correct;
              const showIncorrect =
                phase === "revealed" && isSelected && !choice.correct;
              return (
                <li key={choice.id}>
                  <button
                    type="button"
                    onClick={() => pick(choice.id)}
                    disabled={phase === "revealed"}
                    className={cn(
                      "flex w-full items-start gap-4 rounded-[var(--radius)] border p-4 text-left transition-colors",
                      "border-[var(--color-panel-edge)] bg-[var(--color-panel-raised)]",
                      phase === "answering" &&
                        "hover:border-[var(--color-amber)]",
                      showCorrect &&
                        "border-[var(--color-clear)]/60 bg-[rgb(95_207_128_/_0.08)]",
                      showIncorrect &&
                        "border-[var(--color-critical)]/60 bg-[rgb(229_89_52_/_0.08)]",
                      isSelected &&
                        phase === "answering" &&
                        "border-[var(--color-amber)]",
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "text-callsign mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-[var(--radius-sm)] border text-[10px]",
                        "border-[var(--color-panel-border)] bg-[var(--color-panel-deep)] text-[var(--color-ink-dim)]",
                        showCorrect &&
                          "border-[var(--color-clear)] text-[var(--color-clear)]",
                        showIncorrect &&
                          "border-[var(--color-critical)] text-[var(--color-critical)]",
                      )}
                    >
                      {String.fromCharCode(65 + i)}
                    </span>
                    <span className="flex-1 text-pretty text-sm leading-relaxed text-[var(--color-ink)]">
                      {choice.text}
                    </span>
                    {showCorrect ? (
                      <Check
                        aria-hidden
                        className="mt-1 size-4 text-[var(--color-clear)]"
                      />
                    ) : showIncorrect ? (
                      <X
                        aria-hidden
                        className="mt-1 size-4 text-[var(--color-critical)]"
                      />
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ol>

          {phase === "revealed" ? (
            <div className="mt-6 rounded-[var(--radius)] border border-[var(--color-panel-edge)] bg-[var(--color-panel-deep)] p-5">
              <Callsign
                className={cn(
                  "text-[10px]",
                  wasCorrect
                    ? "text-[var(--color-clear)]"
                    : "text-[var(--color-critical)]",
                )}
              >
                {wasCorrect ? "▸ Correct" : "▸ Reviewed"}
              </Callsign>
              <p className="mt-3 text-pretty text-sm leading-relaxed text-[var(--color-ink-dim)]">
                {card.explanation}
              </p>
            </div>
          ) : null}

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            {phase === "revealed" ? (
              isLast ? (
                <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-[var(--color-ink-dim)]">
                    That&rsquo;s the demo. Sign up to get the full deck and
                    your FSRS-scheduled queue.
                  </p>
                  <div className="flex gap-2">
                    <Button asChild>
                      <a href={signupHref}>
                        Start for free
                        <ArrowRight className="size-4" />
                      </a>
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={restart}
                    >
                      Restart demo
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex w-full items-center justify-end gap-2">
                  <Button type="button" onClick={next}>
                    Next card
                    <ArrowRight className="size-4" />
                  </Button>
                </div>
              )
            ) : (
              <p className="text-callsign text-[10px] text-[var(--color-ink-muted)]">
                Pick an answer to see the explanation.
              </p>
            )}
          </div>
        </article>

        <p className="mt-6 text-center text-xs text-[var(--color-ink-muted)]">
          The real product schedules these for you with FSRS so each card
          comes back exactly when you&rsquo;re about to forget it.{" "}
          <Link
            href="/#method"
            className="text-[var(--color-amber)] underline-offset-4 hover:underline"
          >
            How it works.
          </Link>
        </p>
      </div>
    </section>
  );
}
